<template>
  <div class="flex max-h-[500px] min-h-0 flex-col bg-white">
    <div
      class="flex shrink-0 items-center justify-between gap-2 border-b border-growth-50 px-4 py-3"
    >
      <div class="flex items-center gap-2">
        <font-awesome-icon
          :icon="['fas', 'list-check']"
          class="h-3.5 w-3.5 text-primary-500"
        />
        <h2 class="text-base font-semibold text-growth-900">
          Follow-Up Actions
        </h2>
      </div>
      <ExportMenu
        :exports="[{ type: 'csv', action: exportCsv }]"
        :disabled="!rowData.length"
        size="sm"
      />
    </div>
    <div
      v-if="error"
      class="m-5 flex-1 overflow-y-auto rounded-lg border border-red-light bg-red-tint p-4 text-sm text-red-dark"
      role="alert"
    >
      {{ error }}
      <button type="button" class="btn-link ml-2" @click="refreshData">
        Retry
      </button>
    </div>
    <LoadingIndicator
      v-else-if="loading"
      message="Loading follow-up actions..."
    />
    <div
      v-else-if="!sortedRows.length"
      class="px-5 py-6 text-sm text-growth-600"
    >
      No follow-up actions yet.
    </div>
    <div v-else class="min-h-0 flex-1 overflow-auto">
      <table class="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col class="w-[10%]" />
          <col class="w-[46%]" />
          <col class="w-[10%] max-md:hidden" />
          <col class="w-[14%] max-md:hidden" />
          <col class="w-[11%] max-md:hidden" />
          <col class="w-[9%]" />
        </colgroup>
        <thead class="sticky top-0 z-10 bg-growth-10">
          <tr class="border-b border-growth-50">
            <th
              v-for="column in columns"
              :key="column.key"
              class="px-3 py-2 text-left align-middle text-[11px] font-semibold uppercase tracking-wide text-growth-600"
              :class="column.className"
            >
              <button
                v-if="column.sortable"
                type="button"
                class="inline-flex items-center gap-1.5 text-left focus:outline-none"
                @click="toggleSort(column.key)"
              >
                {{ column.label }}
                <font-awesome-icon
                  v-if="sort.key === column.key"
                  :icon="[
                    'fas',
                    sort.direction === 'asc' ? 'sort-up' : 'sort-down',
                  ]"
                  class="h-3 w-3"
                />
              </button>
              <span v-else>{{ column.label }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="action in sortedRows"
            :key="action.id"
            class="border-b border-growth-50 align-top"
          >
            <td class="py-2.5 pl-4 pr-3">
              <span
                class="inline-flex whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold"
                :class="statusClass(action.status)"
                >{{ statusLabel(action.status) }}</span
              >
            </td>
            <td class="px-3 py-2.5">
              <p
                class="sensitive break-words font-semibold leading-5 text-growth-900"
              >
                {{ action.action_name || action.description }}
              </p>
              <p v-if="action.description && action.description !== action.action_name" class="mt-0.5 break-words text-xs leading-4 text-growth-600">{{ action.description }}</p>
              <p
                v-if="action.notes"
                class="mt-0.5 break-words text-xs leading-4 text-growth-600"
              >
                <span class="font-semibold">Notes</span> <span>{{ action.notes }}</span>
              </p>
              <p v-if="action.constraints?.length" class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs leading-4 text-growth-600">
                <font-awesome-icon :icon="['fas', 'ban']" class="h-3 w-3 shrink-0 text-red-dark" title="Addresses constraint" />
                <router-link v-for="c in action.constraints" :key="c.id" :to="{ path: `/entity/${action.entity}/engagement-tracker`, query: { node: c.id } }" class="sensitive font-semibold text-growth-800 hover:underline" :class="{ 'line-through text-growth-600': c.status === 'resolved' }">{{ c.title }}</router-link>
              </p>
              <p class="mt-0.5 text-xs text-growth-600 md:hidden">
                <span class="sensitive">{{
                  action.owner_name || 'Unassigned'
                }}</span>
                <span v-if="action.due_date">
                  · Due {{ formatDate(action.due_date) }}</span
                >
              </p>
            </td>
            <td class="px-3 py-2.5 text-xs text-growth-800 max-md:hidden">
              {{ action.action_type_name || '—' }}
            </td>
            <td class="px-3 py-2.5 max-md:hidden">
              <button
                type="button"
                class="sensitive inline-flex items-center gap-1.5 text-left text-xs text-growth-800 hover:underline"
                title="Change owner"
                @click="openOwner(action)"
              >
                <span
                  class="sensitive flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-[9px] font-semibold text-white"
                  >{{ action.owner_initials || '—' }}</span
                >
                <span class="sensitive" data-spoof-kind="person">{{ action.owner_name || 'Unassigned' }}</span>
              </button>
            </td>
            <td
              class="px-3 py-2.5 text-xs max-md:hidden"
              :class="dueClass(action)"
            >
              {{ formatDate(action.due_date) || '—' }}
            </td>
            <td class="py-2 pl-2 pr-4 text-right">
              <div class="inline-flex items-center gap-0.5">
                <button
                  type="button"
                  class="row-button"
                  :title="
                    action.status === 'resolved' ? 'Reopen' : 'Mark complete'
                  "
                  @click="requestCompletion(action)"
                >
                  <font-awesome-icon
                    :icon="[
                      'fas',
                      action.status === 'resolved' ? 'rotate-left' : 'check',
                    ]"
                    class="h-3.5 w-3.5"
                  />
                </button>
                <button
                  type="button"
                  class="row-button"
                  title="Edit"
                  @click="openEdit(action)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'pencil']"
                    class="h-3.5 w-3.5"
                  />
                </button>
                <button
                  type="button"
                  class="row-button hover:text-red-dark"
                  title="Delete"
                  @click="requestDelete(action)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'trash']"
                    class="h-3.5 w-3.5"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <ConfirmationModal
      :is-visible="isOpenDeleteActionModal"
      message="Are you sure you want to delete this action item?"
      @confirm="deleteAction"
      @cancel="closeDelete"
    />
    <ConfirmationModal
      :is-visible="isOpenCompleteActionModal"
      :message="completeModalMessage"
      @confirm="completeAction"
      @cancel="closeComplete"
    />
    <Generic_Form
      v-if="isEditModalOpen"
      model="action"
      :object="editActionData"
      mode="Edit"
      :hide-icon="true"
      :is-modal-open="true"
      @update="editAction"
      @close="isEditModalOpen = false"
    />
    <FormUpdateObjectOwner
      v-if="showOwnerModal"
      :object-id="ownerModalData?.objectId"
      :content-type-model="ownerModalData?.objectContentTypeModel"
      :current-owner="ownerModalData?.ownerId"
      @close="onOwnerModalClose"
    />
  </div>
</template>

<script>
import axios from 'axios';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Generic_Form from '@/components/forms/Generic_Form.vue';
import FormUpdateObjectOwner from '@/components/forms/FormUpdateObjectOwner.vue';

export default {
  components: {
    ConfirmationModal,
    ExportMenu,
    LoadingIndicator,
    Generic_Form,
    FormUpdateObjectOwner,
  },
  props: {
    autoHeight: { type: Boolean, default: false },
    entityId: { type: Number, default: null },
    includeDescendants: { type: Boolean, default: true },
    originatingObjectId: { type: [Number, String], default: null },
    originatingObjectContentTypeModel: { type: String, default: null },
    ownerObjectId: { type: [Number, String], default: null },
    ownerContentTypeModel: { type: String, default: null },
    completedDateIsNull: { type: Boolean, default: null },
    sqlFilters: { type: Object, default: () => ({}) },
    theme: { type: Object, default: null },
    shadow: { type: Boolean, default: false },
  },
  data() {
    return {
      rowData: [],
      loading: true,
      error: '',
      fetchController: null,
      sort: { key: 'due_date', direction: 'asc' },
      expandedRows: {},
      isOpenDeleteActionModal: false,
      isOpenCompleteActionModal: false,
      completeActionId: null,
      isActionComplete: false,
      deleteActionId: null,
      editActionData: null,
      isEditModalOpen: false,
      showOwnerModal: false,
      ownerModalData: null,
    };
  },
  computed: {
    columns() {
      return [
        { key: 'status', label: 'Status', className: 'pl-4', sortable: true },
        { key: 'action_name', label: 'Action', className: '', sortable: true },
        {
          key: 'action_type_name',
          label: 'Type',
          className: 'max-md:hidden',
          sortable: true,
        },
        {
          key: 'owner_name',
          label: 'Owner',
          className: 'max-md:hidden',
          sortable: true,
        },
        {
          key: 'due_date',
          label: 'Due',
          className: 'max-md:hidden',
          sortable: true,
        },
        { key: 'details', label: '', className: 'pr-4', sortable: false },
      ];
    },
    tenantScope() {
      return `${this.$store.state.tenant.current_tenant_slug}:${this.$store.getters['auth/current_user']?.id || ''}`;
    },
    queryParams() {
      const p = {};
      if (this.entityId)
        Object.assign(p, {
          entity_id: this.entityId,
          include_descendants: this.includeDescendants,
        });
      if (this.originatingObjectId && this.originatingObjectContentTypeModel)
        Object.assign(p, {
          originating_object_id: this.originatingObjectId,
          originating_object_content_type_model:
            this.originatingObjectContentTypeModel,
        });
      if (this.ownerObjectId && this.ownerContentTypeModel)
        Object.assign(p, {
          owner_object_id: this.ownerObjectId,
          owner_content_type_model: this.ownerContentTypeModel,
        });
      if (this.completedDateIsNull !== null)
        p.completed_date_isnull = this.completedDateIsNull;
      const f = this.sqlFilters || {};
      if (f.originating_object_id)
        p.originating_object_id = f.originating_object_id;
      if (f.originating_object_content_type_model)
        p.originating_object_content_type_model =
          f.originating_object_content_type_model;
      if (f.action_owner_object_id)
        p.owner_object_id = f.action_owner_object_id;
      if (f.action_owner_content_type_model)
        p.owner_content_type_model = f.action_owner_content_type_model;
      if (f.action_completed_date?.['IS NULL'] !== undefined)
        p.completed_date_isnull = true;
      if (f.action_completed_date?.['IS NOT NULL'] !== undefined)
        p.completed_date_isnull = false;
      return p;
    },
    sortedRows() {
      if (this.sort.key === 'details') return this.rowData;
      return [...this.rowData].sort(
        (a, b) =>
          String(a[this.sort.key] ?? '').localeCompare(
            String(b[this.sort.key] ?? ''),
            undefined,
            { numeric: true }
          ) * (this.sort.direction === 'asc' ? 1 : -1)
      );
    },
    completeModalMessage() {
      return this.isActionComplete
        ? 'Mark this action as incomplete?'
        : 'Mark this action as completed?';
    },
  },
  watch: {
    tenantScope: 'resetForScope',
    entityId: 'refreshData',
    originatingObjectId: 'refreshData',
    ownerObjectId: 'refreshData',
    completedDateIsNull: 'refreshData',
    sqlFilters: { deep: true, handler: 'refreshData' },
  },
  mounted() {
    this.refreshData();
  },
  beforeUnmount() {
    this.fetchController?.abort();
  },
  methods: {
    resetForScope() {
      this.rowData = [];
      this.expandedRows = {};
      this.closeDelete();
      this.closeComplete();
      this.isEditModalOpen = false;
      this.showOwnerModal = false;
      this.refreshData();
    },
    toggleSort(key) {
      if (key === 'details') return;
      this.sort = {
        key,
        direction:
          this.sort.key === key && this.sort.direction === 'asc'
            ? 'desc'
            : 'asc',
      };
    },
    statusLabel(status) {
      return (
        {
          open: 'Open',
          in_progress: 'In progress',
          resolved: 'Resolved',
          dropped: 'Dropped',
        }[status] ||
        status ||
        'Open'
      );
    },
    statusClass(status) {
      return (
        {
          open: 'bg-growth-25 text-gray-700',
          in_progress: 'bg-neutral-50 text-neutral-900',
          resolved: 'bg-secondary-100 text-green-dark',
          dropped: 'bg-red-tint text-red-dark',
        }[status] || 'bg-growth-25 text-gray-700'
      );
    },
    formatDate(v) {
      return v
        ? new Date(`${v}T00:00:00`).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '';
    },
    dueClass(a) {
      if (a.status === 'resolved') return 'text-green-dark';
      return a.due_date &&
        new Date(`${a.due_date}T00:00:00`) < new Date().setHours(0, 0, 0, 0)
        ? 'font-semibold text-red-dark'
        : 'text-growth-800';
    },
    async refreshData() {
      this.fetchController?.abort();
      const c = new AbortController(),
        scope = this.tenantScope;
      this.fetchController = c;
      this.error = '';
      this.loading = true;
      this.rowData = [];
      this.expandedRows = {};
      try {
        const r = await axios.get('api/graph-actions/', {
          params: this.queryParams,
          signal: c.signal,
        });
        if (this.fetchController === c && this.tenantScope === scope)
          this.rowData = Array.isArray(r.data) ? r.data : [];
      } catch (e) {
        if (
          !c.signal.aborted &&
          this.fetchController === c &&
          this.tenantScope === scope
        )
          this.error = e.response?.data?.detail || 'Could not load actions.';
      } finally {
        if (this.fetchController === c && this.tenantScope === scope)
          this.loading = false;
      }
    },
    requestCompletion(a) {
      this.completeActionId = a.id;
      this.isActionComplete = a.status === 'resolved';
      this.isOpenCompleteActionModal = true;
    },
    closeComplete() {
      this.isOpenCompleteActionModal = false;
      this.completeActionId = null;
      this.isActionComplete = false;
    },
    async completeAction() {
      await axios.post(
        `api/graph-actions/${this.completeActionId}/complete/`,
        this.isActionComplete ? { incomplete: true } : {}
      );
      this.closeComplete();
      await this.refreshData();
    },
    requestDelete(a) {
      this.deleteActionId = a.id;
      this.isOpenDeleteActionModal = true;
    },
    closeDelete() {
      this.isOpenDeleteActionModal = false;
      this.deleteActionId = null;
    },
    async deleteAction() {
      await axios.delete(`api/graph-actions/${this.deleteActionId}/`);
      this.closeDelete();
      await this.refreshData();
    },
    openEdit(a) {
      this.editActionData = { ...a, action_type: a.action_type };
      this.isEditModalOpen = true;
    },
    openOwner(a) {
      this.ownerModalData = {
        objectId: a.id,
        objectContentTypeModel: 'action',
        ownerId: a.owner_object_id,
      };
      this.showOwnerModal = true;
    },
    editAction() {
      this.isEditModalOpen = false;
      this.refreshData();
    },
    onOwnerModalClose(d) {
      this.showOwnerModal = false;
      if (d.refreshData) this.refreshData();
    },
    exportCsv() {
      const lines = [
        ['Status', 'Description', 'Owner', 'Due'],
        ...this.sortedRows.map(r => [
          this.statusLabel(r.status),
          r.description || r.action_name,
          r.owner_name || 'Unassigned',
          r.due_date || '',
        ]),
      ];
      const csv = lines
        .map(line =>
          line.map(v => `"${String(v).replaceAll('"', '""')}"`).join(',')
        )
        .join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
        link = document.createElement('a');
      link.href = url;
      link.download = 'follow-up-actions.csv';
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
@reference '../../assets/theme.css';
.row-button {
  @apply inline-flex h-6 w-6 items-center justify-center rounded text-growth-600 hover:bg-growth-25 hover:text-growth-900 focus:outline-none;
}
</style>
